import { useEffect, useRef, useState } from "react";

interface TurnstileOptions {
  sitekey: string;
  action?: string;
  language?: string;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __evolutionTurnstilePromise?: Promise<TurnstileApi>;
  }
}

interface TurnstileWidgetProps {
  siteKey?: string;
  resetKey: number;
  error?: string;
  onTokenChange: (token: string) => void;
}

const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (window.__evolutionTurnstilePromise) {
    return window.__evolutionTurnstilePromise;
  }

  window.__evolutionTurnstilePromise = new Promise<TurnstileApi>(
    (resolve, reject) => {
      const handleLoad = () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error("Turnstile no se ha inicializado."));
      };

      const existing = document.querySelector<HTMLScriptElement>(
        "script[data-evolution-turnstile]",
      );
      if (existing) {
        existing.addEventListener("load", handleLoad, { once: true });
        existing.addEventListener(
          "error",
          () => reject(new Error("No se ha podido cargar Turnstile.")),
          { once: true },
        );
        return;
      }

      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.dataset.evolutionTurnstile = "true";
      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error("No se ha podido cargar Turnstile.")),
        { once: true },
      );
      document.head.appendChild(script);
    },
  ).catch((error) => {
    window.__evolutionTurnstilePromise = undefined;
    throw error;
  });

  return window.__evolutionTurnstilePromise;
}

export function TurnstileWidget({
  siteKey,
  resetKey,
  error,
  onTokenChange,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  useEffect(() => {
    if (!siteKey) return undefined;

    let active = true;
    let widgetId: string | undefined;
    setStatus("loading");
    onTokenChange("");

    loadTurnstile()
      .then((api) => {
        if (!active || !containerRef.current) return;
        containerRef.current.replaceChildren();
        widgetId = api.render(containerRef.current, {
          sitekey: siteKey,
          action: "evolution_questionnaire",
          language: "es",
          theme: "light",
          size: "flexible",
          callback: (token) => {
            if (!active) return;
            onTokenChange(token);
          },
          "expired-callback": () => {
            if (!active) return;
            onTokenChange("");
          },
          "error-callback": () => {
            if (!active) return;
            setStatus("error");
            onTokenChange("");
          },
        });
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });

    return () => {
      active = false;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // The widget may already have removed itself after expiry.
        }
      }
    };
  }, [onTokenChange, resetKey, siteKey]);

  if (!siteKey) {
    return (
      <p className="questionnaire-turnstile__message" role="alert">
        La verificación de seguridad no está configurada. Inténtalo de nuevo más
        tarde.
      </p>
    );
  }

  return (
    <div className="questionnaire-turnstile" id="contact-turnstile" tabIndex={-1}>
      <div ref={containerRef} aria-label="Verificación de seguridad" />
      {status === "loading" ? (
        <p className="questionnaire-turnstile__message" role="status">
          Preparando la verificación de seguridad…
        </p>
      ) : null}
      {status === "error" ? (
        <p className="questionnaire-turnstile__message" role="alert">
          No hemos podido completar la verificación. Recarga la página para
          intentarlo de nuevo.
        </p>
      ) : null}
      {error ? (
        <p className="questionnaire-error" id="contact-turnstile-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
